import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import { HeaderSection } from './HeaderSection';
import { InfoItem } from '@components/InfoItem';
import { ProgressBar } from './ProgressBar';
import { CompactProgress } from './CompactProgress';
import { formatFieldValue } from '../utils';
import { useTaskProgress } from '../hooks';
import type {
  WorkOrderViewModel,
  WorkOrderDetailViewModel
} from '@api/enterprise/openapi/models';

type ServiceDetailsProps = {
  workOrder: WorkOrderViewModel;
  details: WorkOrderDetailViewModel[];
  progressText?: string; // Made optional since we're computing it
};

export const ServiceDetails: React.FC<ServiceDetailsProps> = ({
  workOrder,
  details
}) => {
  const { t } = useTranslation('workOrders');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const firstDetail = details?.[0];
  const { totalTasks, completedTasks } = useTaskProgress(details);

  return (
    <HeaderSection
      icon={<BuildIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
      title={t('workOrder.serviceDetails.title')}
    >
      <InfoItem
        label={t('workOrder.serviceDetails.jobType')}
        value={formatFieldValue(workOrder.jobType)}
      />
      <InfoItem
        label={t('workOrder.serviceDetails.scheduledEmployee')}
        value={formatFieldValue(firstDetail?.scheduledEmployee)}
      />
      <InfoItem
        label={t('workOrder.serviceDetails.performedBy')}
        value={formatFieldValue(firstDetail?.performedBy)}
      />
      <Box sx={{ mt: 1, mb: 0.5 }}>
        <InfoItem
          label={t('workOrder.serviceDetails.taskProgress')}
          value={
            <Box sx={{ width: '100%', mt: 1 }}>
              {isMobile ? (
                <CompactProgress
                  completed={completedTasks}
                  total={totalTasks}
                />
              ) : (
                <ProgressBar
                  completed={completedTasks}
                  total={totalTasks}
                  variant={totalTasks <= 6 ? 'stepped' : 'linear'}
                  showPercentage={true}
                  showSteps={!isTablet}
                  itemType="equipment"
                />
              )}
            </Box>
          }
        />
      </Box>
    </HeaderSection>
  );
};
